// src/components/deck_subdeck/CopyModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Copy, Loader2 } from 'lucide-react';
import './MoveModal.css';

interface Cycle {
    _id: string;
    name: string;
    tabs?: { _id: string; name: string }[];
    decks?: { _id: string; name: string }[];
}

interface CopyModalProps {
    mode: 'deck' | 'subdeck';
    // Para deck
    sourceCycleId?: string;
    deckId?: string;
    deckName?: string;
    // Para subdeck
    sourceDeckId?: string;
    subdeckId?: string;
    subdeckName?: string;
    onClose: () => void;
    onSuccess: () => void;
    onToast: (msg: string, type: 'success' | 'error') => void;
}

const CopyModal: React.FC<CopyModalProps> = ({
    mode, sourceCycleId, deckId, deckName,
    sourceDeckId, subdeckId, subdeckName,
    onClose, onSuccess, onToast
}) => {
    const token = () => localStorage.getItem('accessToken') || '';

    const [cycles, setCycles] = useState<Cycle[]>([]);
    const [selectedCycleId, setSelectedCycleId] = useState('');
    const [selectedTabId, setSelectedTabId] = useState('');
    const [selectedDeckId, setSelectedDeckId] = useState('');
    const [tabs, setTabs] = useState<{ _id: string; name: string }[]>([]);
    const [decksInCycle, setDecksInCycle] = useState<{ _id: string; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingTabs, setLoadingTabs] = useState(false);
    const [loadingDecks, setLoadingDecks] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const r = await fetch('/api/cycles', { headers: { Authorization: `Bearer ${token()}` } });
                if (r.ok) {
                    const data = await r.json();
                    // Copiar pode ser para o mesmo ciclo — inclui todos
                    setCycles(data);
                }
            } catch { /* ignore */ }
            finally { setLoading(false); }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedCycleId) { setTabs([]); setDecksInCycle([]); setSelectedTabId(''); setSelectedDeckId(''); return; }

        if (mode === 'deck') {
            setLoadingTabs(true);
            fetch(`/api/cycles/${selectedCycleId}/tabs`, { headers: { Authorization: `Bearer ${token()}` } })
                .then(r => r.ok ? r.json() : [])
                .then(data => { setTabs(data); setSelectedTabId(''); })
                .catch(() => setTabs([]))
                .finally(() => setLoadingTabs(false));
        } else {
            setLoadingDecks(true);
            fetch(`/api/cycles/${selectedCycleId}/decks`, { headers: { Authorization: `Bearer ${token()}` } })
                .then(r => r.ok ? r.json() : { decks: [] })
                .then(data => {
                    setDecksInCycle(data.decks || []);
                    setSelectedDeckId('');
                })
                .catch(() => setDecksInCycle([]))
                .finally(() => setLoadingDecks(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCycleId]);

    const canSubmit = mode === 'deck'
        ? !!selectedCycleId
        : !!selectedCycleId && !!selectedDeckId;

    const handleCopy = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            if (mode === 'deck') {
                const body: any = { targetCycleId: selectedCycleId };
                if (selectedTabId) body.targetTabId = selectedTabId;

                const r = await fetch(`/api/cycles/${sourceCycleId}/decks/${deckId}/copy`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify(body)
                });
                if (r.ok) {
                    onToast('Deck copiado com sucesso!', 'success');
                    onSuccess();
                    onClose();
                } else {
                    const e = await r.json();
                    onToast(e.message || 'Erro ao copiar deck', 'error');
                }
            } else {
                const r = await fetch(`/api/decks/${sourceDeckId}/subdecks/${subdeckId}/copy`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify({ targetDeckId: selectedDeckId })
                });
                if (r.ok) {
                    onToast('Subdeck copiado com sucesso!', 'success');
                    onSuccess();
                    onClose();
                } else {
                    const e = await r.json();
                    onToast(e.message || 'Erro ao copiar subdeck', 'error');
                }
            }
        } catch {
            onToast('Erro ao copiar', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const selectedCycleName = cycles.find(c => c._id === selectedCycleId)?.name || '';

    return (
        <div className="mm-overlay" onClick={onClose}>
            <div className="mm-dialog" onClick={e => e.stopPropagation()}>
                <button className="mm-close" onClick={onClose}><X size={18} /></button>

                <h2 className="mm-title">
                    Copiar {mode === 'deck' ? 'Deck' : 'Subdeck'}
                </h2>

                <div className="mm-source">
                    <span className="mm-source-label">Copiando:</span>
                    <span className="mm-source-name">{mode === 'deck' ? deckName : subdeckName}</span>
                </div>

                {loading ? (
                    <div className="mm-loading"><Loader2 size={20} className="mm-spin" /> Carregando...</div>
                ) : (
                    <>
                        <div className="mm-field">
                            <label className="mm-label">Campo de Estudo destino</label>
                            <select
                                className="mm-select"
                                value={selectedCycleId}
                                onChange={e => setSelectedCycleId(e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {cycles.map(c => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {mode === 'deck' && selectedCycleId && (
                            loadingTabs ? (
                                <div className="mm-loading-small"><Loader2 size={16} className="mm-spin" /> Carregando abas...</div>
                            ) : tabs.length > 0 ? (
                                <div className="mm-field">
                                    <label className="mm-label">Aba destino</label>
                                    <select
                                        className="mm-select"
                                        value={selectedTabId}
                                        onChange={e => setSelectedTabId(e.target.value)}
                                    >
                                        <option value="">Sem aba específica</option>
                                        {tabs.map(t => (
                                            <option key={t._id} value={t._id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <p className="mm-info">Este campo não tem abas — a cópia será adicionada diretamente.</p>
                            )
                        )}

                        {mode === 'subdeck' && selectedCycleId && (
                            loadingDecks ? (
                                <div className="mm-loading-small"><Loader2 size={16} className="mm-spin" /> Carregando decks...</div>
                            ) : decksInCycle.length > 0 ? (
                                <div className="mm-field">
                                    <label className="mm-label">Deck destino em "{selectedCycleName}"</label>
                                    <select
                                        className="mm-select"
                                        value={selectedDeckId}
                                        onChange={e => setSelectedDeckId(e.target.value)}
                                    >
                                        <option value="">Selecione um deck...</option>
                                        {decksInCycle.map(d => (
                                            <option key={d._id} value={d._id}>{d.name}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <p className="mm-info">Este campo não tem decks disponíveis.</p>
                            )
                        )}

                        <div className="mm-actions">
                            <button className="mm-btn-cancel" onClick={onClose}>Cancelar</button>
                            <button
                                className="mm-btn-move"
                                onClick={handleCopy}
                                disabled={!canSubmit || submitting}
                            >
                                {submitting
                                    ? <><Loader2 size={15} className="mm-spin" /> Copiando...</>
                                    : <><Copy size={15} /> Copiar</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CopyModal;
